import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import {
  Monitor, Power, Settings, Copy, Trash2, CheckCircle, UserPlus, RefreshCw, ChevronUp, ChevronDown, X,
} from "react-feather";
import {
  Button, Container, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import {
  getScreenPage, assignMerchantToScreen, activateScreen, deactivateScreen, deleteScreen,
  cloneScreenConfig, getMerchantListing, fetchBranches,
} from "../../utils/apiCalls";
import { useNavigate } from "react-router-dom";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Loader as RLoader, TablePagination } from "../reporting/ReportingShared";

const EMPTY_COUNTS = { total: 0, active: 0, pending: 0, inactive: 0 };
const DEFAULT_SORT = { by: "createdAt", dir: "desc" };

// `deviceInfo` is a JSON column; some MySQL/MariaDB setups hand it back as a string.
const deviceInfoOf = (screen) => {
  const raw = screen?.deviceInfo;
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const ScreenList = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const isAdmin = userType === "admin";
  const { selectedMerchant: headerMerchant } = useSelector((state) => state.merchant || {});

  // One page from the server, plus the status breakdown of everything the other filters match.
  const [screens, setScreens] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [isFetching, setIsFetching] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [onlineFilter, setOnlineFilter] = useState("all");
  // Admins start on whatever merchant the header dropdown has selected, and follow it when it
  // changes, so this page agrees with the rest of the panel. Merchant users are scoped server-side.
  const [merchantFilter, setMerchantFilter] = useState(
    isAdmin && headerMerchant && headerMerchant !== "all" ? String(headerMerchant) : "all"
  );
  const [branchFilter, setBranchFilter] = useState("all");
  const [filterMerchants, setFilterMerchants] = useState([]);
  const [filterBranches, setFilterBranches] = useState([]);

  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Screens the clone modal can copy onto. Fetched when it opens, because the table only holds a page.
  const [cloneTargets, setCloneTargets] = useState([]);

  // Assign merchant modal (admin only, for pending screens)
  const [assignModal, setAssignModal] = useState(false);
  const [assignScreen, setAssignScreen] = useState(null);
  const [assignMerchantId, setAssignMerchantId] = useState("");
  const [merchantsList, setMerchantsList] = useState([]);

  // Activate modal (merchant: pick branch, admin: pick merchant + branch)
  const [activateModal, setActivateModal] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [branches, setBranches] = useState([]);
  const [activateForm, setActivateForm] = useState({
    MerchantId: "",
    branchId: "",
    name: "",
  });

  // Clone modal
  const [cloneModal, setCloneModal] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);
  const [cloneTarget, setCloneTarget] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (isAdmin) setMerchantFilter(headerMerchant && headerMerchant !== "all" ? String(headerMerchant) : "all");
  }, [isAdmin, headerMerchant]);

  useEffect(() => {
    if (!isAdmin) return;
    getMerchantListing().then((list) => setFilterMerchants(list || []));
  }, [isAdmin]);

  // Branches follow the merchant: an admin sees none until a merchant is picked, a merchant user
  // sees their own. The branch filter is cleared with the merchant so it never points elsewhere.
  useEffect(() => {
    setBranchFilter("all");
    if (isAdmin && merchantFilter === "all") {
      setFilterBranches([]);
      return;
    }
    fetchBranches(isAdmin ? { MerchantId: merchantFilter } : {}, false).then((res) =>
      setFilterBranches(res?.data || [])
    );
  }, [isAdmin, merchantFilter]);

  // Any filter or sort change goes back to the first page; staying on page 4 of a narrower list
  // usually lands on an empty page.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, onlineFilter, merchantFilter, branchFilter, sort, pageSize]);

  // Only the latest request may write state, so a slow response cannot overwrite a newer one.
  const requestSeq = useRef(0);

  const fetchScreens = useCallback(async () => {
    const seq = ++requestSeq.current;
    setIsFetching(true);
    const data = await getScreenPage({
      page,
      pageSize,
      sortBy: sort.by,
      sortDir: sort.dir,
      search: debouncedSearch,
      status: statusFilter,
      online: onlineFilter,
      merchantId: isAdmin ? merchantFilter : undefined,
      branchId: branchFilter,
    });
    if (seq !== requestSeq.current) return;
    setScreens(data?.rows || []);
    setTotal(data?.total || 0);
    setCounts(data?.counts || EMPTY_COUNTS);
    setIsFetching(false);
  }, [page, pageSize, sort, debouncedSearch, statusFilter, onlineFilter, merchantFilter, branchFilter, isAdmin]);

  useEffect(() => {
    fetchScreens();
  }, [fetchScreens]);

  const toggleSort = (by) => {
    setSort((prev) =>
      prev.by === by ? { by, dir: prev.dir === "asc" ? "desc" : "asc" } : { by, dir: by === "lastPingAt" ? "desc" : "asc" }
    );
  };

  const filtersActive =
    !!search || statusFilter !== "all" || onlineFilter !== "all" || branchFilter !== "all" ||
    (isAdmin && merchantFilter !== "all");

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setOnlineFilter("all");
    setBranchFilter("all");
    if (isAdmin) setMerchantFilter("all");
  };

  // --- Assign Merchant (admin only) ---
  const openAssignModal = async (screen) => {
    setAssignScreen(screen);
    setAssignMerchantId("");
    const merchantData = await getMerchantListing();
    setMerchantsList(merchantData || []);
    setAssignModal(true);
  };

  const handleAssignMerchant = async () => {
    if (!assignMerchantId) return;
    const result = await assignMerchantToScreen(assignScreen.id, assignMerchantId);
    if (result) {
      setAssignModal(false);
      fetchScreens();
    }
  };

  // --- Activate (merchant: branch only, admin: merchant + branch) ---
  const openActivateModal = async (screen) => {
    setSelectedScreen(screen);
    setActivateForm({ MerchantId: "", branchId: "", name: "" });
    setBranches([]);

    if (isAdmin) {
      const merchantData = await getMerchantListing();
      setMerchants(merchantData || []);
      if (screen.merchant?.id) {
        setActivateForm((prev) => ({ ...prev, MerchantId: String(screen.merchant.id) }));
        const branchData = await fetchBranches({ MerchantId: screen.merchant.id });
        setBranches(branchData?.data || []);
      }
    } else {
      const branchData = await fetchBranches({});
      setBranches(branchData?.data || []);
    }

    setActivateModal(true);
  };

  const handleMerchantChange = async (merchantId) => {
    setActivateForm({ ...activateForm, MerchantId: merchantId, branchId: "" });
    if (merchantId) {
      const branchData = await fetchBranches({ MerchantId: merchantId });
      setBranches(branchData?.data || []);
    } else {
      setBranches([]);
    }
  };

  const handleActivate = async () => {
    if (!activateForm.branchId) return;
    if (isAdmin && !activateForm.MerchantId && !selectedScreen?.merchant?.id) return;

    const payload = { branchId: activateForm.branchId, name: activateForm.name };
    if (isAdmin && activateForm.MerchantId) {
      payload.MerchantId = activateForm.MerchantId;
    }

    const result = await activateScreen(selectedScreen.id, payload);
    if (result) {
      setActivateModal(false);
      fetchScreens();
    }
  };

  const handleDeactivate = async (screenId) => {
    if (window.confirm("Are you sure you want to deactivate this screen?")) {
      const result = await deactivateScreen(screenId);
      if (result) fetchScreens();
    }
  };

  const handleDelete = async (screenId) => {
    if (window.confirm("Are you sure you want to delete this screen? This cannot be undone.")) {
      const result = await deleteScreen(screenId);
      if (result) fetchScreens();
    }
  };

  const openCloneModal = async (screen) => {
    setCloneSource(screen);
    setCloneTarget("");
    setCloneTargets([]);
    setCloneModal(true);
    // Same merchant only: category and product overrides refer to that merchant's menu, so copying
    // them onto another merchant's screen would produce overrides for items it does not have.
    const data = await getScreenPage({
      page: 1,
      pageSize: 100,
      sortBy: "name",
      sortDir: "asc",
      status: "active",
      merchantId: isAdmin ? screen.merchant?.id : undefined,
    });
    setCloneTargets((data?.rows || []).filter((s) => s.id !== screen.id));
  };

  const handleClone = async () => {
    if (!cloneTarget) return;
    const result = await cloneScreenConfig(cloneSource.id, cloneTarget);
    if (result) {
      setCloneModal(false);
      fetchScreens();
    }
  };

  // ---- Shared pieces ----
  const SortHeader = ({ by, children, style }) => {
    const active = sort.by === by;
    const Icon = active && sort.dir === "asc" ? ChevronUp : ChevronDown;
    return (
      <th
        style={{ ...tbl.th, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", ...style }}
        onClick={() => toggleSort(by)}
        title="Sort"
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: active ? C.coral : undefined }}>
          {children}
          <Icon size={12} style={{ opacity: active ? 1 : 0.3 }} />
        </span>
      </th>
    );
  };

  // A tile doubles as a status filter: clicking the one already selected clears it.
  const tileFilter = (status) => ({
    onClick: () => setStatusFilter((prev) => (prev === status ? "all" : status)),
    style: {
      cursor: "pointer",
      borderRadius: 16,
      outline: statusFilter === status ? `2px solid ${C.coral}` : "none",
      outlineOffset: 2,
    },
  });
  const StatusPill = ({ status }) => {
    const map = {
      active: { bg: C.greenLight, fg: C.green, label: "Active" },
      pending: { bg: C.orangeLight, fg: C.orange, label: "Pending" },
      inactive: { bg: C.coralLight, fg: C.coral, label: "Inactive" },
    };
    const s = map[status] || map.pending;
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "4px 10px",
          borderRadius: 20,
          fontSize: 11,
          fontWeight: 700,
          background: s.bg,
          color: s.fg,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.fg }} />
        {s.label}
      </span>
    );
  };

  const OnlinePill = ({ isOnline }) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: isOnline ? C.greenLight : C.coralLight,
        color: isOnline ? C.green : C.coral,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: isOnline ? C.green : C.coral,
        }}
      />
      {isOnline ? "Online" : "Offline"}
    </span>
  );

  const ActionIcon = ({ icon: Icon, color, title, onClick, disabled }) => (
    <div
      onClick={disabled ? undefined : onClick}
      title={title}
      style={{
        width: 32,
        height: 32,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all 0.15s",
        border: `1px solid ${C.border}`,
        background: C.white,
        opacity: disabled ? 0.55 : 1,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = C.coralLight;
        e.currentTarget.style.borderColor = C.coral;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = C.white;
        e.currentTarget.style.borderColor = C.border;
      }}
    >
      <Icon size={15} color={color} />
    </div>
  );

  const filterSelectStyle = {
    padding: "7px 12px",
    fontSize: 12,
    borderRadius: 8,
    border: `1.5px solid ${C.border}`,
    fontFamily: "inherit",
    outline: "none",
    color: C.text,
    background: C.white,
    cursor: "pointer",
    minWidth: 140,
    transition: "border 0.15s",
  };

  const searchInputStyle = { ...filterSelectStyle, cursor: "text", minWidth: 240 };

  const modalHeaderStyle = { borderBottom: `2px solid ${C.borderLight}` };
  const modalHeaderTitleStyle = { fontWeight: 700, color: C.text };

  const modalLabelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: C.textLight,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
    display: "block",
  };

  const modalInputStyle = {
    borderRadius: 8,
    border: `1.5px solid ${C.border}`,
    padding: "10px 12px",
    fontSize: 13,
    width: "100%",
  };

  const primaryBtnStyle = {
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    border: "none",
    borderRadius: 8,
    padding: "8px 20px",
    fontWeight: 600,
    color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
  };

  const successBtnStyle = {
    background: `linear-gradient(135deg, ${C.green}, #38F9D7)`,
    border: "none",
    borderRadius: 8,
    padding: "8px 20px",
    fontWeight: 600,
    color: C.white,
    boxShadow: "0 4px 12px rgba(46,204,113,0.25)",
  };

  const infoBtnStyle = {
    background: `linear-gradient(135deg, ${C.blue}, #A8B8FF)`,
    border: "none",
    borderRadius: 8,
    padding: "8px 20px",
    fontWeight: 600,
    color: C.white,
    boxShadow: "0 4px 12px rgba(102,126,234,0.25)",
  };

  return (
    <Fragment>
      <Breadcrumb title="Screens" parent="Digital Signage" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {/* KPI Tiles */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 16,
              marginBottom: 24,
            }}
          >
            <div {...tileFilter("all")}>
              <Tile label="Total Screens" value={counts.total} icon="🖥️" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
            </div>
            <div {...tileFilter("active")}>
              <Tile label="Active" value={counts.active} icon="✅" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={60} />
            </div>
            <div {...tileFilter("pending")}>
              <Tile label="Pending" value={counts.pending} icon="⏳" gradient={`linear-gradient(135deg, ${C.orange}, #FFD200)`} delay={120} />
            </div>
            <div {...tileFilter("inactive")}>
              <Tile label="Inactive" value={counts.inactive} icon="⛔" gradient={`linear-gradient(135deg, #E74C3C, #FF6B6B)`} delay={180} />
            </div>
          </div>

          {/* Filter bar + table */}
          <RCard>
            {/* Header row: what this is and how many, with refresh on the right. */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: C.text,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Monitor size={16} color={C.coral} />
                {isAdmin ? "All Screens" : "My Screens"}
              </span>

              <span
                style={{
                  fontSize: 12,
                  color: C.textLight,
                  background: C.bg,
                  padding: "4px 12px",
                  borderRadius: 20,
                }}
              >
                {total} {total === 1 ? "screen" : "screens"}
              </span>
              <div style={{ flex: 1 }} />
              <div
                onClick={fetchScreens}
                title="Refresh"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  border: `1.5px solid ${C.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.15s",
                  background: C.white,
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = C.coral;
                  e.currentTarget.style.background = C.coralLight;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = C.border;
                  e.currentTarget.style.background = C.white;
                }}
              >
                <RefreshCw size={15} color={C.textMed} />
              </div>
            </div>

            {/* Filter row. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <input
                placeholder="Search code, name, merchant, branch..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={searchInputStyle}
              />

              {isAdmin && (
                <select
                  value={merchantFilter}
                  onChange={(e) => setMerchantFilter(e.target.value)}
                  style={{ ...filterSelectStyle, minWidth: 180 }}
                >
                  <option value="all">All Merchants</option>
                  {filterMerchants.map((m) => (
                    <option key={m.id} value={String(m.id)}>
                      {m.name}
                    </option>
                  ))}
                </select>
              )}

              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                disabled={isAdmin && merchantFilter === "all"}
                title={isAdmin && merchantFilter === "all" ? "Pick a merchant first" : undefined}
                style={{
                  ...filterSelectStyle,
                  minWidth: 160,
                  opacity: isAdmin && merchantFilter === "all" ? 0.55 : 1,
                  cursor: isAdmin && merchantFilter === "all" ? "not-allowed" : "pointer",
                }}
              >
                <option value="all">All Branches</option>
                {filterBranches.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {b.name}
                  </option>
                ))}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={filterSelectStyle}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="inactive">Inactive</option>
              </select>

              <select
                value={onlineFilter}
                onChange={(e) => setOnlineFilter(e.target.value)}
                style={{ ...filterSelectStyle, minWidth: 120 }}
              >
                <option value="all">Online & Offline</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
              </select>

              {filtersActive && (
                <button
                  onClick={clearFilters}
                  style={{
                    ...filterSelectStyle,
                    minWidth: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    color: C.textMed,
                  }}
                >
                  <X size={13} /> Clear
                </button>
              )}
            </div>

            {isFetching && screens.length === 0 ? (
              <RLoader />
            ) : screens.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: C.textLight, fontSize: 13 }}>
                {filtersActive
                  ? "No screens match your filters."
                  : isAdmin
                    ? "No screens registered yet. Install the WagsSignage app on a device to register."
                    : "No screens assigned to you yet. Contact admin to assign screens."}
              </div>
            ) : (
              <>
              {/* Kept on screen while the next page loads, dimmed, so the table does not jump. */}
              <div style={{ opacity: isFetching ? 0.55 : 1, transition: "opacity 0.15s" }}>
              <div
                style={{
                  overflowX: "auto",
                  borderRadius: 10,
                  border: `1px solid ${C.border}`,
                }}
              >
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <SortHeader by="screenCode">Screen Code</SortHeader>
                      <SortHeader by="name">Name</SortHeader>
                      <SortHeader by="status">Status</SortHeader>
                      {isAdmin && <SortHeader by="merchant">Merchant</SortHeader>}
                      <SortHeader by="branch">Branch</SortHeader>
                      <th style={tbl.th}>Online</th>
                      <SortHeader by="lastPingAt">Last Active</SortHeader>
                      <th style={tbl.th}>App</th>
                      <SortHeader by="createdAt">Registered</SortHeader>
                      <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {screens.map((screen) => (
                      <tr key={screen.id} className="rpt-row">
                        <td
                          style={{
                            ...tbl.td,
                            fontFamily: "monospace",
                            fontWeight: 700,
                            color: C.text,
                          }}
                        >
                          {screen.screenCode}
                        </td>
                        <td style={{ ...tbl.td, color: C.text, fontWeight: 600 }}>{screen.name || "—"}</td>
                        <td style={tbl.td}>
                          <StatusPill status={screen.status} />
                        </td>
                        {isAdmin && (
                          <td style={{ ...tbl.td, color: C.textMed }}>{screen.merchant?.name || "—"}</td>
                        )}
                        <td style={{ ...tbl.td, color: C.textMed }}>{screen.branch?.name || "—"}</td>
                        <td style={tbl.td}>
                          <OnlinePill isOnline={screen.isOnline} />
                        </td>
                        <td style={{ ...tbl.td, color: C.textLight, fontSize: 12 }}>
                          {screen.lastPingAt ? new Date(screen.lastPingAt).toLocaleString() : "Never"}
                        </td>
                        {/* Reported by the screen on each heartbeat. Older builds send nothing. */}
                        <td style={{ ...tbl.td, fontSize: 12 }}>
                          {(() => {
                            const info = deviceInfoOf(screen);
                            if (!info?.appVersion) return <span style={{ color: C.textLight }}>—</span>;
                            const os = info.osVersion
                              ? `${info.platform === "ios" ? "iOS" : "Android"} ${info.osVersion}`
                              : null;
                            const hardware = [info.model, os].filter(Boolean).join(" · ");
                            return (
                              <>
                                <div style={{ color: C.text, fontWeight: 600, fontFamily: "monospace" }}>
                                  {info.appVersion}
                                  {info.appBuild ? ` (${info.appBuild})` : ""}
                                </div>
                                {hardware && <div style={{ color: C.textLight }}>{hardware}</div>}
                              </>
                            );
                          })()}
                        </td>
                        <td style={{ ...tbl.td, color: C.textLight, fontSize: 12 }}>
                          {screen.createdAt ? new Date(screen.createdAt).toLocaleDateString() : "—"}
                        </td>
                        <td style={tbl.td}>
                          <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
                            {isAdmin && screen.status === "pending" && (
                              <ActionIcon
                                icon={UserPlus}
                                color={C.blue}
                                title="Assign Merchant"
                                onClick={() => openAssignModal(screen)}
                              />
                            )}
                            {screen.status === "inactive" && (
                              <ActionIcon
                                icon={CheckCircle}
                                color={C.green}
                                title="Activate"
                                onClick={() => openActivateModal(screen)}
                              />
                            )}
                            {screen.status === "active" && (
                              <>
                                <ActionIcon
                                  icon={Settings}
                                  color={C.coral}
                                  title="Configure"
                                  onClick={() => navigate(`/signage/screens/configure/${screen.id}`)}
                                />
                                <ActionIcon
                                  icon={Copy}
                                  color={C.purple}
                                  title="Clone Config"
                                  onClick={() => openCloneModal(screen)}
                                />
                                <ActionIcon
                                  icon={Power}
                                  color={C.orange}
                                  title="Deactivate"
                                  onClick={() => handleDeactivate(screen.id)}
                                />
                              </>
                            )}
                            {isAdmin && (
                              <ActionIcon
                                icon={Trash2}
                                color={C.coral}
                                title="Delete"
                                onClick={() => handleDelete(screen.id)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
              <TablePagination
                total={total}
                page={page}
                limit={pageSize}
                onPageChange={setPage}
                onLimitChange={setPageSize}
              />
              </>
            )}
          </RCard>
        </div>
      </Container>

      {/* Assign Merchant Modal (admin only) */}
      <Modal isOpen={assignModal} toggle={() => setAssignModal(false)} centered>
        <ModalHeader toggle={() => setAssignModal(false)} style={modalHeaderStyle}>
          <span style={modalHeaderTitleStyle}>Assign Merchant: {assignScreen?.screenCode}</span>
        </ModalHeader>
        <ModalBody style={{ padding: 24 }}>
          <label style={modalLabelStyle}>Merchant *</label>
          <select
            value={assignMerchantId}
            onChange={(e) => setAssignMerchantId(e.target.value)}
            style={modalInputStyle}
          >
            <option value="">Select Merchant</option>
            {merchantsList.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.reference})
              </option>
            ))}
          </select>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setAssignModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignMerchant}
            disabled={!assignMerchantId}
            style={{ ...infoBtnStyle, opacity: !assignMerchantId ? 0.55 : 1 }}
          >
            Assign Merchant
          </Button>
        </ModalFooter>
      </Modal>

      {/* Activate Modal */}
      <Modal isOpen={activateModal} toggle={() => setActivateModal(false)} centered>
        <ModalHeader toggle={() => setActivateModal(false)} style={modalHeaderStyle}>
          <span style={modalHeaderTitleStyle}>Activate Screen: {selectedScreen?.screenCode}</span>
        </ModalHeader>
        <ModalBody style={{ padding: 24 }}>
          <div style={{ marginBottom: 16 }}>
            <label style={modalLabelStyle}>Screen Name</label>
            <input
              type="text"
              placeholder="e.g., Lobby Display 1"
              value={activateForm.name}
              onChange={(e) => setActivateForm({ ...activateForm, name: e.target.value })}
              style={modalInputStyle}
            />
          </div>
          {isAdmin && (
            <div style={{ marginBottom: 16 }}>
              <label style={modalLabelStyle}>Merchant *</label>
              <select
                value={activateForm.MerchantId}
                onChange={(e) => handleMerchantChange(e.target.value)}
                style={modalInputStyle}
              >
                <option value="">Select Merchant</option>
                {merchants.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.reference})
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={modalLabelStyle}>Branch *</label>
            <select
              value={activateForm.branchId}
              onChange={(e) => setActivateForm({ ...activateForm, branchId: e.target.value })}
              disabled={isAdmin && !activateForm.MerchantId}
              style={{
                ...modalInputStyle,
                opacity: isAdmin && !activateForm.MerchantId ? 0.55 : 1,
              }}
            >
              <option value="">Select Branch</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} {b.arabicName ? `(${b.arabicName})` : ""}
                </option>
              ))}
            </select>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setActivateModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleActivate}
            disabled={!activateForm.branchId || (isAdmin && !activateForm.MerchantId && !selectedScreen?.merchant?.id)}
            style={{
              ...successBtnStyle,
              opacity:
                !activateForm.branchId ||
                (isAdmin && !activateForm.MerchantId && !selectedScreen?.merchant?.id)
                  ? 0.55
                  : 1,
            }}
          >
            Activate
          </Button>
        </ModalFooter>
      </Modal>

      {/* Clone Modal */}
      <Modal isOpen={cloneModal} toggle={() => setCloneModal(false)} centered>
        <ModalHeader toggle={() => setCloneModal(false)} style={modalHeaderStyle}>
          <span style={modalHeaderTitleStyle}>Clone Config from: {cloneSource?.screenCode}</span>
        </ModalHeader>
        <ModalBody style={{ padding: 24 }}>
          <label style={modalLabelStyle}>Target Screen</label>
          <select
            value={cloneTarget}
            onChange={(e) => setCloneTarget(e.target.value)}
            style={modalInputStyle}
          >
            <option value="">Select Target Screen</option>
            {cloneTargets.map((s) => (
              <option key={s.id} value={s.id}>
                {s.screenCode} - {s.name || "Unnamed"}
                {s.branch?.name ? ` · ${s.branch.name}` : ""}
              </option>
            ))}
          </select>
          <div style={{ marginTop: 12, fontSize: 12, color: C.textLight }}>
            This will overwrite all category, product, and promotion settings on the target screen.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setCloneModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleClone}
            disabled={!cloneTarget}
            style={{ ...primaryBtnStyle, opacity: !cloneTarget ? 0.55 : 1 }}
          >
            Clone Config
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default ScreenList;
